/**
 * Appels de la base d'aliments, de la recherche unifiée et de l'accès rapide
 * (specs §6.3 et §6.4, archi §6.2).
 *
 * ⚠️ **Aucun appel à Open Food Facts ici.** Le front n'atteint OFF que par le
 * serveur (§5.2) : `searchFoods` et `lookupBarcode` interrogent des routes de
 * *notre* API, qui vont chercher chez OFF si besoin. Voir `api/off.ts` pour le
 * proxy explicite.
 *
 * ⚠️ Le client n'envoie jamais de macros pour une entrée de journal (§5.1). Ce
 * qu'il envoie ici, ce sont les macros **de référence pour 100 g** d'une fiche
 * d'aliment — ce n'est pas la même chose, et le serveur recalcule toujours le
 * total d'une entrée à partir d'elles.
 */

import type {
  BarcodeLookupResponse,
  FoodDTO,
  FoodInput,
  FoodSearchResponse,
  QuickAccessResponse,
  UpdateFoodBody,
} from '../../../shared/types';
import { apiFetch } from './client';

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/* --- Accès rapide : un seul aller-retour (§6.2) ------------------------- */

/**
 * Clé de cache de l'accès rapide.
 *
 * Déclarée ici plutôt que dans le composant : `react-refresh` interdit à un
 * module de composants d'exporter autre chose, et cette clé est partagée — le
 * hook du journal l'invalide après chaque mutation, les « récents » en
 * dépendant.
 */
export const QUICK_ACCESS_KEY = ['quick-access'] as const;

export function fetchQuickAccess(): Promise<QuickAccessResponse> {
  return apiFetch<QuickAccessResponse>('/api/quick-access');
}

/* --- Recherche et scan -------------------------------------------------- */

/**
 * Recherche unifiée : la base personnelle d'abord, Open Food Facts ensuite
 * (specs §6.3-b, §7.2). Les deux listes arrivent **séparées** : c'est le serveur
 * qui a déjà retiré des résultats OFF ceux que la base personnelle couvre.
 */
export function searchFoods(query: string): Promise<FoodSearchResponse> {
  return apiFetch<FoodSearchResponse>(`/api/foods/search?q=${encodeURIComponent(query)}`);
}

/**
 * Lookup d'un code-barres — « perso d'abord, puis Open Food Facts » (§6.4).
 *
 * ⚠️ Ne renvoie **jamais** d'erreur pour un code inconnu : `personal` et `off`
 * valent alors `null`, et l'écran propose de créer une fiche pré-remplie.
 */
export function lookupBarcode(code: string): Promise<BarcodeLookupResponse> {
  return apiFetch<BarcodeLookupResponse>(`/api/foods/barcode/${encodeURIComponent(code)}`);
}

/* --- Base personnelle --------------------------------------------------- */

export function fetchFoods(query = ''): Promise<FoodDTO[]> {
  return apiFetch<FoodDTO[]>(`/api/foods?q=${encodeURIComponent(query)}`);
}

export function fetchFood(id: number): Promise<FoodDTO> {
  return apiFetch<FoodDTO>(`/api/foods/${String(id)}`);
}

/**
 * Création — et **import depuis OFF** quand `offCode` est renseigné (specs §6.4).
 * Rejouer avec le même `offCode` renvoie la fiche existante plutôt qu'un doublon.
 */
export function createFood(body: FoodInput): Promise<FoodDTO> {
  return send<FoodDTO>('POST', '/api/foods', body);
}

/** Correction d'une fiche. **Sans effet sur le journal déjà enregistré** (§4.1). */
export function updateFood(id: number, body: UpdateFoodBody): Promise<FoodDTO> {
  return send<FoodDTO>('PATCH', `/api/foods/${String(id)}`, body);
}

export function deleteFood(id: number): Promise<void> {
  return send<void>('DELETE', `/api/foods/${String(id)}`);
}
